import React from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { getLegalDoc } from '../../legal/documents';
import { LegalBlock } from '../../legal/types';
import SeoHead from '@components/SeoHead';

const renderBlock = (block: LegalBlock, index: number) => {
  switch (block.type) {
    case 'ul':
      return (
        <ul key={index} className="list-disc pl-6 space-y-1.5 text-text-secondary">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case 'note':
      return (
        <div
          key={index}
          className="flex gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{block.text}</span>
        </div>
      );
    default:
      return (
        <p key={index} className="text-text-secondary leading-relaxed">
          {block.text}
        </p>
      );
  }
};

const LegalPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const doc = getLegalDoc(slug);

  if (!doc) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-4 py-8">
      <SeoHead
        title={doc.title}
        description={`${doc.title} for RoomZa — roommate matching and shared housing in South Africa.`}
        path={`/legal/${doc.slug}`}
      />
      <div className="max-w-3xl mx-auto">
        <div className="mb-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-text-secondary hover:text-primary-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <Link to="/" className="text-sm font-semibold text-pink-600 hover:text-pink-700">
            Home
          </Link>
        </div>

        <article className="bg-white/90 backdrop-blur-sm rounded-2xl border-2 border-gray-100 shadow-lg p-6 md:p-10">
          <h1 className="text-2xl md:text-3xl font-extrabold text-text-primary">{doc.title}</h1>
          {(doc.effectiveDate || doc.lastUpdated) && (
            <p className="text-xs text-text-muted mt-2">
              {doc.effectiveDate && <>Effective date: {doc.effectiveDate}</>}
              {doc.effectiveDate && doc.lastUpdated && <> &middot; </>}
              {doc.lastUpdated && <>Last updated: {doc.lastUpdated}</>}
            </p>
          )}

          {doc.intro?.map((paragraph, i) => (
            <p key={i} className="text-text-secondary leading-relaxed mt-4">
              {paragraph}
            </p>
          ))}

          <div className="mt-6 space-y-8">
            {doc.sections.map((section, i) => (
              <section key={i} className="space-y-3">
                {section.heading && (
                  <h2 className="text-lg md:text-xl font-bold text-text-primary">{section.heading}</h2>
                )}
                {section.blocks.map(renderBlock)}
              </section>
            ))}
          </div>
        </article>
      </div>
    </div>
  );
};

export default LegalPage;
